import { useState } from "react";
import type { Promotion } from "@/types";
import { formatDate } from "@/utils/format";

interface PromotionGridProps {
  promotions: Promotion[];
  onDelete: (id: string) => void;
}

const PromotionGrid = ({ promotions, onDelete }: PromotionGridProps) => {
  const [confirmId, setConfirmId] = useState<string | null>(null);

  if (promotions.length === 0) {
    return (
      <div className="glass-panel rounded-lg px-5 py-12 text-center">
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-megaphone-line text-2xl" />
        </span>
        <p className="text-sm text-foreground-700 font-semibold">No hay promociones activas</p>
        <p className="text-xs text-foreground-500 mt-1">
          Crea tu primera oferta y difúndela a toda tu red de asesores en un clic.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {promotions.map((p) => {
        const active = new Date(p.endDate) >= new Date();
        return (
          <article key={p.id} className="glass-panel rounded-lg p-5 animate-fade-up flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <span className="w-11 h-11 rounded-lg bg-accent-500/20 text-accent-200 flex items-center justify-center shrink-0">
                <i className="ri-price-tag-3-line text-xl" />
              </span>
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-primary-500/25 text-primary-100">
                -{p.discountPct}%
              </span>
            </div>

            <h3 className="editable-title text-base font-bold mt-4">{p.title}</h3>
            <p className="text-xs md:text-sm text-foreground-600 mt-2 flex-1">{p.description || "Sin descripción."}</p>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full ${
                  active ? "bg-accent-500/20 text-accent-200" : "bg-background-200/60 text-foreground-600"
                }`}
              >
                {active ? "Activa" : "Finalizada"}
              </span>
              <span className="text-[10px] text-foreground-500">
                {formatDate(p.startDate)} — {formatDate(p.endDate)}
              </span>
            </div>

            <div className="mt-4 pt-3 border-t border-background-300/25">
              {confirmId === p.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-foreground-600 flex-1">¿Eliminar promoción?</span>
                  <button
                    onClick={() => {
                      onDelete(p.id);
                      setConfirmId(null);
                    }}
                    className="px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                  >
                    Sí, eliminar
                  </button>
                  <button
                    onClick={() => setConfirmId(null)}
                    className="px-3 py-1.5 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmId(p.id)}
                  className="text-[11px] font-semibold text-foreground-500 hover:text-primary-200 cursor-pointer inline-flex items-center gap-1.5"
                >
                  <i className="ri-delete-bin-line" /> Eliminar
                </button>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default PromotionGrid;